// Global ReminderContext: schedules booking reminders across all pages.
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import api from './api';
import AuthContext from './AuthContext';

const ReminderContext = createContext();

export const ReminderProvider = ({ children }) => {
  const { token } = useContext(AuthContext);
  const [settings, setSettings] = useState({ enabled: true, minutesBefore: 15 });
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState(''); // legacy simple message (kept for compatibility)
  const [modalBooking, setModalBooking] = useState(null); // detailed booking object for rich modal
  const [todayBookings, setTodayBookings] = useState([]);
  const shownIdsRef = useRef(new Set()); // bookings already reminded (avoid repeat)
  const getLocalDateString = (d = new Date()) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  const currentDate = getLocalDateString(new Date());

  const closeModal = () => { setModalVisible(false); setModalBooking(null); };

  const triggerReminder = useCallback((booking) => {
    if (!booking) return;
    if (!(booking.status === 'pending' || booking.status === 'confirmed')) return; // ignore completed/canceled
    if (shownIdsRef.current.has(booking.id)) return; // already shown once
    // Store booking for detailed display
    setModalBooking(booking);
    setModalMessage(`ถึงเวลานัดหมายของคุณ: ${booking.customer_name}`); // short text
    setModalVisible(true);
    shownIdsRef.current.add(booking.id);
  }, []);

  const schedule = useCallback(() => { /* removed local mode scheduling */ }, []);

  const loadTodayBookings = useCallback(async () => {
    try {
      const resp = await api.get('/bookings', { params: { date: currentDate } });
      setTodayBookings(resp.data || []);
    } catch (e) {
      // silent fail for global fetch
    }
  }, [currentDate]);

  // remove client mode refresh

  // Server polling for global reminders - check every 60 seconds and pause when tab is hidden
  useEffect(() => {
    if (!settings.enabled || !token) return; // require auth token to poll
    let timerId = null;

    const poll = async () => {
      if (document.hidden) return;
      try {
        const resp = await api.get('/reminders/due');
        const list = resp.data || [];
        list.forEach(b => {
          if (!shownIdsRef.current.has(b.id)) {
            triggerReminder(b);
          }
        });
      } catch (err) {
        console.error('[Reminder] Poll error:', err);
      }
    };

    const POLL_INTERVAL = 180000; // 180s (3 minutes) to minimize Cloudflare Workers CPU and DB quota

    const schedulePoll = () => {
      if (timerId) clearInterval(timerId);
      if (!document.hidden) {
        timerId = setInterval(() => {
          if (!document.hidden) {
            poll();
          }
        }, POLL_INTERVAL);
      }
    };

    if (!document.hidden) {
      poll();
    }
    schedulePoll();

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        poll();
        schedulePoll();
      } else {
        if (timerId) clearInterval(timerId);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timerId) clearInterval(timerId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [settings.enabled, token, triggerReminder]);

  // Reschedule when bookings or settings change.
  useEffect(() => { schedule(); }, [schedule]);

  // Optional: request notification permission once when enabling.
  useEffect(() => {
    if (settings.enabled && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => { });
    }
  }, [settings.enabled]);

  const updateSettings = async (partial) => {
    try {
      // push to backend for global store
      const next = { ...settings, ...partial };
      if (token) {
        await api.put('/reminder/settings', { enabled: next.enabled, minutes_before: next.minutesBefore });
      }
      setSettings(next);
    } catch {
      setSettings(prev => ({ ...prev, ...partial }));
    }
  };

  // load settings from backend on mount
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const resp = await api.get('/reminder/settings');
        setSettings({ enabled: !!resp.data.enabled, minutesBefore: resp.data.minutes_before ?? 15 });
      } catch { }
    })();
  }, [token]);

  return (
    <ReminderContext.Provider value={{
      settings,
      updateSettings,
      triggerReminder,
      modalVisible,
      modalMessage,
      modalBooking,
      closeModal
    }}>
      {children}
    </ReminderContext.Provider>
  );
};

export const useReminder = () => {
  const ctx = useContext(ReminderContext);
  if (!ctx) throw new Error('useReminder must be used within ReminderProvider');
  return ctx;
};